import { isDefined } from "@clipboard-health/util-ts";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Box, ListItemButton, ListItemText } from "@mui/material";
import { Text } from "@src/appV2/redesign/components/Text";
import { parseISO } from "date-fns";
// eslint-disable-next-line no-restricted-imports -- legacy, use @clipboard-health/date-time
import { formatInTimeZone } from "date-fns-tz";
import { forwardRef } from "react";

import { type ShiftSearchResult } from "../api/useSearchShift";

interface ShiftResultItemProps {
  shift: ShiftSearchResult;
  selected: boolean;
  expanded: boolean;
  onClick: () => void;
}

function getShiftStatus(shift: ShiftSearchResult): string {
  if (shift.deleted) {
    return "Cancelled";
  }
  if (isDefined(shift.agent?.userId)) {
    return "Filled";
  }
  return "Open";
}

export const ShiftResultItem = forwardRef<HTMLDivElement, ShiftResultItemProps>((props, ref) => {
  const { shift, selected, expanded, onClick } = props;

  const timezone = shift.facility?.tmz ?? "America/Los_Angeles";
  const shiftDate = formatInTimeZone(parseISO(shift.start), timezone, "MMM d, yyyy");
  const shiftStartTime = formatInTimeZone(parseISO(shift.start), timezone, "h:mm a");
  const shiftEndTime = formatInTimeZone(parseISO(shift.end), timezone, "h:mm a");
  const status = getShiftStatus(shift);

  return (
    <ListItemButton
      ref={ref}
      selected={selected}
      sx={{
        py: 1.5,
        px: 2,
        "&.Mui-selected": {
          backgroundColor: "action.selected",
        },
      }}
      onClick={onClick}
    >
      <ListItemText
        primary={
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Text sx={{ fontWeight: 500 }}>
              {shift.agentReq} - {status}
            </Text>
            {selected &&
              (expanded ? (
                <ExpandMoreIcon fontSize="small" />
              ) : (
                <ChevronRightIcon fontSize="small" />
              ))}
          </Box>
        }
        secondary={
          <Text variant="caption" color="text.secondary">
            {shiftDate} {shiftStartTime} - {shiftEndTime}
          </Text>
        }
      />
    </ListItemButton>
  );
});

ShiftResultItem.displayName = "ShiftResultItem";
